import type { FaceData } from '../../index'
import type { MeasurementFlowPhase } from '../types'
import { ROTATION_X_THRESHOLD, ROTATION_Y_THRESHOLD } from './constants'

/**
 * The guidance gates a single tracked frame can fail, in priority order. `null`
 * means the frame cleared every gate and the caller decides what a "good" frame
 * means next (PD/SH stable sampling, or SH's `findingFrame`).
 */
export type GuidancePhase = Extract<
  MeasurementFlowPhase,
  'detecting' | 'tooClose' | 'tooFar' | 'positioning' | 'opening'
>

type GuidanceOptions = {
  distanceRange: { min: number; max: number }
  eyeOpenThreshold: number
  /**
   * Pose thresholds in degrees. Defaults to the shared constants, which are
   * deliberately *tighter* than the native gates: before sampling starts they
   * guide toward a good pose rather than explain a rejection.
   *
   * A caller mid-capture should pass the native gates instead, so the message
   * and the acceptance agree — otherwise it nags about a pose native is happily
   * taking while the progress bar advances.
   */
  rotationRange?: { xDeg: number; yDeg: number }
}

/**
 * Maps one native `FaceData` frame onto the corrective guidance phase it should
 * surface, shared by every capture hook so the gate ladder (detected → distance
 * → head pose → eyes) is defined once instead of re-inlined per measurement.
 */
/**
 * The distance rung on its own, so it can stay live through sampling where the
 * rest of the ladder cannot.
 *
 * `null` on an undetected frame rather than `detecting`: a caller mid-capture
 * has a better phase to fall back to than "look at the camera".
 */
export const deriveDistancePhase = (
  incoming: FaceData,
  distanceRange: { min: number; max: number }
): Extract<GuidancePhase, 'tooClose' | 'tooFar'> | null => {
  if (!incoming.detected) return null
  if (incoming.distanceCm < distanceRange.min) return 'tooClose'
  if (incoming.distanceCm > distanceRange.max) return 'tooFar'
  return null
}

export const deriveGuidancePhase = (
  incoming: FaceData,
  { distanceRange, eyeOpenThreshold, rotationRange }: GuidanceOptions
): GuidancePhase | null => {
  if (!incoming.detected) return 'detecting'
  const distancePhase = deriveDistancePhase(incoming, distanceRange)
  if (distancePhase) return distancePhase

  const isCentered =
    Math.abs(incoming.rotationX) <
      (rotationRange?.xDeg ?? ROTATION_X_THRESHOLD) &&
    Math.abs(incoming.rotationY) < (rotationRange?.yDeg ?? ROTATION_Y_THRESHOLD)
  if (!isCentered) return 'positioning'

  const hasOpenEyes =
    (incoming.eyeOpenness?.left ?? 0) >= eyeOpenThreshold &&
    (incoming.eyeOpenness?.right ?? 0) >= eyeOpenThreshold
  if (!hasOpenEyes) return 'opening'

  return null
}
